import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Bot, X, Send, Mic, MicOff, Volume2, VolumeX,
  Loader2, Mail, Calendar, MessageSquare, ChevronDown,
  Camera, History, Plus, Trash2, Maximize2, Minimize2,
  Zap, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { speak, stopSpeaking } from '@/lib/tts';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssistantButton() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: "Salut ! C'est Valentine, ton assistante de direction. Parle-moi ou tape — je suis là pour t'aider.\n\nDis-moi ce dont tu as besoin, je m'en occupe !",
    timestamp: new Date()
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [voiceGender, setVoiceGender] = useState(localStorage.getItem('assistantVoice') || 'femme');
  const [mediaPlaying, setMediaPlaying] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const messagesEndRef = useRef(null);
  const sendMessageRef = useRef(null);
  const inputRef = useRef(null);
  const mediaPlayerRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Load conversation history
  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/brain/conversations');
      if (res.data?.conversations) setConversations(res.data.conversations);
    } catch {}
  }, []);

  // Load a specific conversation
  const loadConversation = async (convId) => {
    try {
      const res = await api.get(`/brain/conversations/${convId}`);
      if (res.data?.messages) {
        setMessages(res.data.messages.map(m => ({
          ...m,
          timestamp: new Date(m.timestamp || m.created_at || Date.now())
        })));
        setActiveConvId(convId);
        setShowHistory(false);
      }
    } catch {
      toast.error('Erreur chargement conversation');
    }
  };

  const startNewConversation = () => {
    setActiveConvId(null);
    setMessages([{
      role: 'assistant',
      content: "Nouvelle conversation ! C'est Valentine, qu'est-ce qu'on fait ?",
      timestamp: new Date()
    }]);
    setShowHistory(false);
  };

  // Saisie vocale : micro + détection de silence + transcription Whisper
  const voice = useVoiceRecorder({
    onText: (t) => sendMessageRef.current?.(t),
    onError: (m) => toast.error(m),
  });
  const isRecording = voice.etat === 'listening';
  const isTranscribing = voice.etat === 'transcribing';
  useEffect(() => {
    if ('speechSynthesis' in window) window.speechSynthesis.getVoices();
    return () => stopSpeaking();
  }, []);

  // Auto-pause media during speech
  useEffect(() => {
    if (isRecording && mediaPlayerRef.current && !mediaPlayerRef.current.paused)
      mediaPlayerRef.current.volume = 0.05;
    else if (!isRecording && mediaPlayerRef.current && mediaPlaying)
      mediaPlayerRef.current.volume = 0.4;
  }, [isRecording, mediaPlaying]);

  const toggleRecording = () => {
    if (isTranscribing) return;
    if (isSpeaking) { stopSpeaking(); setIsSpeaking(false); }
    if (isRecording) voice.stop();
    else { setInputMessage(''); voice.start(); }
  };

  const handleMediaCommand = (command) => {
    const action = command.action;
    if (action === 'stop' && mediaPlayerRef.current) { mediaPlayerRef.current.pause(); mediaPlayerRef.current.src = ''; setMediaPlaying(null); }
    if (action === 'play_radio' && command.station?.url) {
      if (!mediaPlayerRef.current) mediaPlayerRef.current = new Audio();
      mediaPlayerRef.current.src = command.station.url;
      mediaPlayerRef.current.volume = 0.4;
      mediaPlayerRef.current.play().catch(() => {});
      setMediaPlaying({ type: 'radio', name: command.station.name });
    }
    if (action === 'play_music') {
      setMediaPlaying({ type: 'music', name: command.genre?.name || 'Musique' });
      window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(command.genre?.youtube_search || 'musique')}`, '_blank');
    }
  };

  const sendMessage = async (text = inputMessage) => {
    if (!text.trim()) return;
    const userMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    stopSpeaking();
    try {
      const res = await api.post('/brain/chat', {
        message: text,
        agent_type: 'chef_exploitation',
        conversation_id: activeConvId || undefined,
      });
      let response = res.data.response;
      setSuggestions(res.data.suggestions || []);
      if (res.data.conversation_id && !activeConvId) setActiveConvId(res.data.conversation_id);
      const actions = res.data.actions || [];
      const mediaAction = actions.find(a => a.type === 'media_command');
      if (mediaAction?.command) handleMediaCommand(mediaAction.command);
      if (res.data.generated_document?.type === 'workspace_switch') {
        const newSocieteId = res.data.generated_document.societe_id;
        if (newSocieteId) {
          localStorage.setItem('activeSocieteId', newSocieteId);
          sessionStorage.setItem('activeSocieteIdOnglet', newSocieteId);
          toast.success(`Workspace : ${res.data.generated_document.societe_nom}`);
          window.dispatchEvent(new CustomEvent('societe-changed', { detail: { societe_id: newSocieteId, societe_nom: res.data.generated_document.societe_nom } }));
          setTimeout(() => window.location.reload(), 1500);
        }
      }
      const assistantMessage = { role: 'assistant', content: response, timestamp: new Date() };
      setMessages(prev => [...prev, assistantMessage]);
      if (autoSpeak && response) { setIsSpeaking(true); speak(response, () => setIsSpeaking(false)); }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Oups, petit bug technique. Réessaie !", timestamp: new Date(), isError: true }]);
    }
    setIsLoading(false);
  };

  sendMessageRef.current = sendMessage;

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const quickActions = [
    { icon: Zap, label: 'Briefing', cmd: 'Valentine, donne-moi mon briefing du jour' },
    { icon: Mail, label: 'Emails', cmd: 'Quelles sont mes urgences mails ?' },
    { icon: Calendar, label: 'Agenda', cmd: "Qu'est-ce que j'ai au programme aujourd'hui?" },
    { icon: MessageSquare, label: 'Recap', cmd: 'Donne-moi mon recap du jour' },
  ];

  // === CLOSED STATE ===
  if (!isOpen) {
    return (
      <button
        onClick={() => { setIsOpen(true); loadConversations(); }}
        className="fixed bottom-36 md:bottom-24 right-4 md:right-8 z-[60] group"
        data-testid="assistant-fab"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-emerald-500/30 rounded-full animate-ping" />
          <div className="relative flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-full shadow-2xl hover:shadow-emerald-500/25 hover:scale-110 transition-all duration-300">
            <Bot className="w-8 h-8" />
          </div>
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-bounce">IA</div>
        </div>
      </button>
    );
  }

  const panelWidth = isExpanded ? 'w-[600px]' : 'w-[420px]';
  const chatHeight = isExpanded ? 'h-[500px]' : 'h-[340px]';

  // === FULL CHAT PANEL ===
  return (
    <div className={`fixed bottom-6 right-4 md:right-8 z-[60] ${panelWidth} max-w-[calc(100vw-2rem)] transition-all duration-300`} data-testid="assistant-panel">
      <Card className="shadow-2xl border-0 overflow-hidden rounded-2xl bg-white">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">Valentine</CardTitle>
                <p className="text-[11px] opacity-80">
                  {isRecording ? 'Je t\'écoute... (je coupe après 2 s de silence)' :
                   isTranscribing ? 'Je transcris...' :
                   isSpeaking ? 'Je parle...' :
                   mediaPlaying ? `${mediaPlaying.name}` :
                   isLoading ? 'Je réfléchis...' :
                   'Assistante de direction — vocal & texte'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadConversations(); }}
                title="Historique" data-testid="btn-history">
                <History className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => { setAutoSpeak(!autoSpeak); if (isSpeaking) stopSpeaking(); setIsSpeaking(false); }}
                title={autoSpeak ? 'Couper la voix' : 'Activer la voix'}>
                {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? 'Réduire' : 'Agrandir'}>
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => { setIsOpen(false); stopSpeaking(); }} data-testid="btn-close-assistant">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* History Panel */}
          {showHistory && (
            <div className="border-b bg-gray-50 p-3 max-h-[250px] overflow-y-auto" data-testid="history-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-600">Conversations récentes</p>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={startNewConversation} data-testid="btn-new-conv">
                  <Plus className="w-3 h-3 mr-1" />Nouvelle
                </Button>
              </div>
              {conversations.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Aucune conversation</p>
              ) : (
                <div className="space-y-1">
                  {conversations.slice(0, 15).map(conv => (
                    <div
                      key={conv.id}
                      className={cn(
                        'flex items-center gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors',
                        activeConvId === conv.id ? 'bg-emerald-100 text-emerald-800' : 'hover:bg-gray-100'
                      )}
                      onClick={() => loadConversation(conv.id)}
                      data-testid={`conv-${conv.id}`}
                    >
                      <MessageSquare className="w-3 h-3 shrink-0 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{conv.title || 'Sans titre'}</p>
                        <p className="text-[10px] text-gray-400">{conv.agent_nom || ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Voice Settings */}
          <div className="flex gap-1.5 p-2 border-b bg-gray-50/70 items-center">
            <Button variant={voiceGender === 'femme' ? 'default' : 'outline'} size="sm" className="h-6 text-[10px] px-2"
              data-testid="voice-femme-btn"
              onClick={() => { setVoiceGender('femme'); localStorage.setItem('assistantVoice', 'femme'); }}>
              Voix F
            </Button>
            <Button variant={voiceGender === 'homme' ? 'default' : 'outline'} size="sm" className="h-6 text-[10px] px-2"
              data-testid="voice-homme-btn"
              onClick={() => { setVoiceGender('homme'); localStorage.setItem('assistantVoice', 'homme'); }}>
              Voix H
            </Button>
            <div className="flex-1" />
            {quickActions.map((a, i) => (
              <Button key={i} variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-gray-500 hover:text-emerald-700"
                onClick={() => sendMessage(a.cmd)} disabled={isLoading}>
                <a.icon className="w-3 h-3 mr-0.5" />{a.label}
              </Button>
            ))}
          </div>

          {/* Messages */}
          <ScrollArea className={cn(chatHeight, 'px-3 py-2')} data-testid="chat-messages">
            <div className="space-y-3">
              {messages.map((msg, idx) => (
                <div key={idx} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div className={cn(
                    'max-w-[85%] rounded-2xl px-3.5 py-2',
                    msg.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-sm'
                      : msg.isError
                        ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  )}>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    <p className={cn('text-[9px] mt-1',
                      msg.role === 'user' ? 'text-white/60' : 'text-gray-400'
                    )}>
                      {msg.timestamp instanceof Date
                        ? msg.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : ''}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-gray-500">Je réfléchis...</span>
                    </div>
                  </div>
                </div>
              )}
              {isSpeaking && (
                <div className="flex justify-center">
                  <button
                    onClick={() => { stopSpeaking(); setIsSpeaking(false); }}
                    className="flex items-center gap-1.5 text-[10px] text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full hover:bg-emerald-100 transition-colors"
                  >
                    <Volume2 className="w-3 h-3 animate-pulse" />
                    Lecture en cours — cliquer pour arrêter
                  </button>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input Bar */}
          {suggestions.length > 0 && !isLoading && (
            <div className="px-2.5 pt-2 flex flex-wrap gap-1.5 border-t bg-white" data-testid="assistant-suggestions">
              {suggestions.slice(0, 4).map((sug, i) => (
                <button
                  key={sug}
                  type="button"
                  data-testid={`assistant-suggestion-${i}`}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                  onClick={() => {
                    if (sug.endsWith('…')) { setInputMessage(sug.replace('…', '').trim() + ' : '); inputRef.current?.focus(); }
                    else sendMessage(sug);
                  }}
                >
                  {sug}
                </button>
              ))}
            </div>
          )}
          <div className="p-2.5 border-t bg-white">
            <div className="flex gap-1.5">
              <Button
                variant={isRecording ? 'destructive' : 'outline'}
                size="icon"
                onClick={toggleRecording}
                disabled={isLoading || isTranscribing}
                className={cn('shrink-0 h-9 w-9 rounded-full', isRecording && 'animate-pulse ring-2 ring-red-300')}
                data-testid="btn-mic"
              >
                {isTranscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="icon" className="shrink-0 h-9 w-9 rounded-full" data-testid="photo-parcelle-btn"
                title="Photo / Vidéo"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file'; input.accept = 'image/*,video/*'; input.capture = 'environment';
                  input.onchange = async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    setIsLoading(true);
                    const isVideo = file.type.startsWith('video/');
                    const isImage = file.type.startsWith('image/');
                    setMessages(prev => [...prev, { role: 'user', content: `${isVideo ? 'Vidéo' : 'Photo'}: ${file.name}`, timestamp: new Date() }]);
                    try {
                      const token = localStorage.getItem('token');
                      const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');

                      if (isVideo) {
                        // Upload vidéo via brain/upload puis envoyer au chat
                        const formData = new FormData(); formData.append('file', file);
                        const uploadRes = await fetch(`${API_URL}/api/brain/upload`, {
                          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
                        });
                        const uploadData = await uploadRes.json();
                        if (uploadData.file_id) {
                          const chatRes = await api.post('/brain/chat', {
                            message: `Analyse cette vidéo : ${file.name}`,
                            agent_type: 'chef_exploitation',
                            conversation_id: activeConvId || undefined,
                            file_ids: [uploadData.file_id],
                          });
                          const response = chatRes.data.response;
                          if (chatRes.data.conversation_id) setActiveConvId(chatRes.data.conversation_id);
                          setMessages(prev => [...prev, { role: 'assistant', content: response, timestamp: new Date() }]);
                          if (autoSpeak && response) { setIsSpeaking(true); speak(response.substring(0, 400), () => setIsSpeaking(false)); }
                        } else {
                          setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur upload vidéo.', timestamp: new Date(), isError: true }]);
                        }
                      } else {
                        // Photo : analyse parcelle (existant)
                        const formData = new FormData(); formData.append('photo', file);
                        if (societeId) formData.append('societe_id', societeId);
                        const res = await fetch(`${API_URL}/api/exploitation/vision/analyze`, {
                          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, ...(societeId ? { 'X-Societe-Id': societeId } : {}) }, body: formData
                        });
                        const data = await res.json();
                        const analysis = data.analysis || {}; const diag = analysis.diagnostic || {};
                        let text = `Diagnostic: ${diag.etat_general || 'inconnu'}\n${diag.description || ''}`;
                        if (analysis.problemes_detectes?.length) { text += '\n\nProblèmes:'; analysis.problemes_detectes.forEach(p => { text += `\n- ${p.nom} (${p.gravite}): ${p.description}`; }); }
                        if (analysis.traitements_recommandes?.length) { text += '\n\nTraitements:'; analysis.traitements_recommandes.forEach(t => { text += `\n- ${t.produit} (${t.type})`; }); }
                        setMessages(prev => [...prev, { role: 'assistant', content: text, timestamp: new Date() }]);
                        if (autoSpeak) { setIsSpeaking(true); speak(text.substring(0, 300), () => setIsSpeaking(false)); }
                      }
                    } catch { setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur analyse fichier.', timestamp: new Date(), isError: true }]); }
                    setIsLoading(false);
                  };
                  input.click();
                }}>
                <Camera className="w-4 h-4" />
              </Button>
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isRecording ? 'Parlez...' : isTranscribing ? 'Transcription...' : 'Tapez votre message...'}
                disabled={isLoading || isRecording || isTranscribing}
                className="flex-1 h-9 rounded-full text-sm border-gray-200 focus:border-emerald-400 focus:ring-emerald-400"
                data-testid="assistant-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || isLoading}
                size="icon"
                className="shrink-0 h-9 w-9 rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
